import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList, Trash2, RefreshCw, Filter, Zap, Shield, Clock,
  Power, Radio, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import { getLogs, clearLogs } from '@/lib/api';

const TYPE_CONFIG = {
  relay_control:     { label: 'Manuel Röle',  color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',   icon: Zap },
  relay_control_all: { label: 'Toplu Kontrol', color: 'bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30', icon: Power },
  scheduler:         { label: 'Zamanlayıcı',  color: 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30', icon: Clock },
  auth:              { label: 'Oturum',        color: 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30', icon: Shield },
};

const formatTs = (ts) => {
  try {
    return new Date(ts).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'medium' });
  } catch { return ts; }
};

const LogRow = ({ log }) => {
  const cfg = TYPE_CONFIG[log.type] || TYPE_CONFIG.auth;
  const Icon = cfg.icon;
  return (
    <div
      className="bg-card border border-border rounded-xl px-4 py-3 flex items-start gap-3 hover:border-primary/20 transition-all"
      data-testid={`log-row-${log.id}`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${log.action === 'on' ? 'bg-green-500/10' : log.action === 'off' ? 'bg-destructive/10' : 'bg-muted'}`}>
        <Icon size={15} className={log.action === 'on' ? 'text-green-500' : log.action === 'off' ? 'text-destructive' : 'text-muted-foreground'} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-snug">{log.description}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-xs text-muted-foreground font-mono-tech">{formatTs(log.timestamp)}</span>
          {log.relay_numbers?.length > 0 && log.relay_numbers.length <= 4 && (
            <div className="flex gap-1">
              {log.relay_numbers.map(n => (
                <Badge key={n} variant="outline" className="text-[10px] font-mono-tech px-1 gap-0.5 h-4">
                  <Zap size={8} />R{n}
                </Badge>
              ))}
            </div>
          )}
          {log.relay_numbers?.length > 4 && (
            <Badge variant="outline" className="text-[10px] px-1.5 h-4">
              {log.relay_numbers.length} röle
            </Badge>
          )}
        </div>
      </div>

      <div className="shrink-0 flex flex-col items-end gap-1.5">
        <Badge className={`text-[10px] px-2 h-5 ${cfg.color}`}>{cfg.label}</Badge>
        {log.action && (
          <Badge className={`text-[10px] px-2 h-5 ${log.action === 'on' ? 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30' : 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30'}`}>
            {log.action === 'on' ? 'AÇ' : 'KAPAT'}
          </Badge>
        )}
      </div>
    </div>
  );
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'Tümü' },
  { value: 'relay_control', label: 'Manuel Röle' },
  { value: 'relay_control_all', label: 'Toplu Kontrol' },
  { value: 'scheduler', label: 'Zamanlayıcı' },
  { value: 'auth', label: 'Oturum' },
];

const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    try {
      const res = await getLogs(typeFilter, 200);
      setLogs(res.data);
    } catch {
      toast.error('Loglar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    setLoading(true);
    setPage(0);
    load();
  }, [load]);

  const handleClear = async () => {
    try {
      await clearLogs();
      setLogs([]);
      toast.success('Tüm loglar temizlendi');
    } catch {
      toast.error('Loglar temizlenemedi');
    }
  };

  const paginated = logs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(logs.length / PAGE_SIZE);

  const counts = {
    relay_control: logs.filter(l => l.type === 'relay_control').length,
    relay_control_all: logs.filter(l => l.type === 'relay_control_all').length,
    scheduler: logs.filter(l => l.type === 'scheduler').length,
    auth: logs.filter(l => l.type === 'auth').length,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{fontFamily:'Outfit,sans-serif'}}>Aktivite Logu</h1>
          <p className="text-muted-foreground text-sm mt-1">Tüm röle kontrol ve sistem olayları</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={load} data-testid="refresh-logs-btn">
            <RefreshCw size={16} />
          </Button>
          <ConfirmDeleteDialog
            title="Tüm Logları Temizle"
            description="Tüm aktivite kayıtları silinecek. Bu işlem geri alınamaz."
            onConfirm={handleClear}
            trigger={
              <Button variant="outline" className="gap-2 text-destructive hover:text-destructive" data-testid="clear-logs-btn">
                <Trash2 size={14} /> Temizle
              </Button>
            }
          />
        </div>
      </div>

      {/* Summary badges */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="log-summary">
        {Object.entries(counts).map(([type, count]) => {
          const cfg = TYPE_CONFIG[type];
          const Icon = cfg.icon;
          return (
            <div key={type} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium ${cfg.color}`}>
              <Icon size={12} /> {cfg.label}: {count}
            </div>
          );
        })}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-5">
        <Filter size={14} className="text-muted-foreground" />
        <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(0); }}>
          <SelectTrigger className="w-48" data-testid="log-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTER_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{logs.length} kayıt</span>
      </div>

      {/* Log list */}
      {loading ? (
        <div className="space-y-2">
          {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl" data-testid="empty-logs">
          <ClipboardList size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-muted-foreground font-medium">Henüz kayıt yok</p>
          <p className="text-sm text-muted-foreground mt-1">Röleleri kontrol ettiğinizde kayıtlar burada görünecek</p>
        </div>
      ) : (
        <>
          <div className="space-y-2" data-testid="logs-list">
            {paginated.map(log => <LogRow key={log.id} log={log} />)}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-5">
              <Button variant="outline" size="icon" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} data-testid="prev-page-btn">
                <ChevronLeft size={16} />
              </Button>
              <span className="text-sm text-muted-foreground">{page + 1} / {totalPages}</span>
              <Button variant="outline" size="icon" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} data-testid="next-page-btn">
                <ChevronRight size={16} />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Logs;
